import React, { useState } from 'react';
import {
  reduxForm,
  Field,
  Form,
  InjectedFormProps,
  getFormValues
} from 'redux-form';
import { compose } from 'ramda';
import { Theme, createStyles, makeStyles, Grid } from '@material-ui/core';
import { Paper, FieldPercent, Modal } from 'components/Base';
import Fields from 'components/Fields';
import Router from 'components/Router/Router';
import StoreProvider from 'store/store';
import { MetaType } from 'store/modules/Meta/types';
import { BoxWrapper, Button } from 'components/Base';
import { CustomerType } from 'store/modules/Customers/types';
import QuestionnaireRequest from './QuestionnaireRequest/QuestionnaireRequest';
import classNames from 'classnames';

export interface RiskProfileTypes {
  knowledgeAndExperience: MetaType;
  riskLevels: MetaType;
  caseCustomers: Array<CustomerType>;
  timeHorizons: Array<MetaType>;
  hasCapacityForLossEnabled: boolean;
  hasRiskRequirementCapacityEnabled: boolean;
  values: any;
  createRiskProfile: Function;
}

const { connect, selectors } = StoreProvider;
const { useParams, useHistory } = Router;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3, 3)
    },
    wrappedField: {
      marginTop: '21px',
      marginBottom: '21px',
      marginLeft: '8px',
      paddingRight: '8px'
    },
    leftColumn: {
      display: 'flex',
      '@media(min-width: 600)': {
        maxWidth: '362px'
      }
    },
    riskProfile: {
      color: '#fff'
    },
    btn: {
      marginTop: theme.spacing(3)
    },
    header: {
      background: '#fff'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1
    },
    field: {
      flex: 1
    },
    questionnareBox: {
      height: '4.125rem',
      borderBottom: '1px solid #eaeaea',
      display: 'flex',
      alignItems: 'center',
      padding: '1rem'
    },
    questionnareEmailModal: {
      padding: '7px 14px',
      transition: 'all 0.3s ease-in-out',
      overflow: 'hidden',
      height: '300px',
      width: '300px',
      position: 'relative'
    },
    questionnaireEmailBtn: {
      padding: '7px 14px',
      position: 'absolute',
      bottom: '12px',
      right: '12px'
    },
    riskWrapper: {
      padding: '2.1rem 1rem',
      borderRadius: '4px'
    },
    sendEmail: {
      marginBottom: '8px',
      padding: '2rem 1rem'
    },
    roundedBox: {
      boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
      borderRadius: ' 25px',
      margin: '1rem'
    },
    labelNoWrap: {
      '& .MuiInputLabel-root': {
        whiteSpace: 'nowrap'
      }
    }
  })
);

const RiskProfile = ({
  knowledgeAndExperience,
  riskLevels,
  handleSubmit,
  caseCustomers,
  timeHorizons,
  hasCapacityForLossEnabled,
  hasRiskRequirementCapacityEnabled,
  values,
  createRiskProfile
}: RiskProfileTypes & InjectedFormProps) => {
  const [sendEmail, setSendEmail] = useState(false);
  const classes = useStyles();
  const history = useHistory();
  const { portfolio } = useParams() as any;
  const isMoreThanOneCustomer = caseCustomers.length > 1;
  const customers =
    isMoreThanOneCustomer &&
    (caseCustomers.filter(
      (customer: CustomerType) => !customer.is_jointed
    ) as CustomerType[]);
  const firstCustomer = customers && customers[0];
  const secondCustomer = customers && customers[1];

  return (
    <Grid
      xs={12}
      sm={12}
      md={4}
      lg={4}
      xl={3}
      item
      className={classes.leftColumn}
    >
      <Form onSubmit={handleSubmit} className={classes.form}>
        <BoxWrapper>
          <div className={classes.questionnareBox}>
            <Button color="primary" onClick={() => setSendEmail(!sendEmail)}>
              ATR Questionnaire
            </Button>
            {hasCapacityForLossEnabled && (
              <Button
                style={{ marginLeft: '24px' }}
                color="primary"
                onClick={async () => {
                  await createRiskProfile();
                  history.push(
                    `/${portfolio}/${caseCustomers[0]?.id}/capacity-for-loss`
                  );
                }}
              >
                CFL Questionnaire
              </Button>
            )}
          </div>
          <div
            style={{
              paddingLeft: '1rem',
              paddingRight: '1rem',
              paddingTop: '1rem'
            }}
          >
            <Field
              name="customer_objectives"
              shrink
              className={classes.wrappedField}
              fullWidth
              component={Fields.Text}
              multiline
              minRows={1}
              label="Client objectives"
              tooltip="Optional"
            />

            <Field
              name="time_horizon_interval"
              shrink
              className={classes.wrappedField}
              fullWidth
              options={timeHorizons}
              component={Fields.Select}
              label="Investment time horizon (years)"
            />

            {hasRiskRequirementCapacityEnabled && (
              <FieldPercent
                name="required_return"
                shrink
                className={classes.wrappedField}
                fullWidth
                clearfieldinform="riskProfile"
                validate={[Fields.Validators.isPercentageValid]}
                component={Fields.Text}
                label="Required gross investment return p.a."
                tooltip="Gross annual investment return required on invested assets in order to achieve financial planning goal(s)"
              />
            )}

            {isMoreThanOneCustomer ? (
              <>
                <Field
                  name="risk_tolerance"
                  shrink
                  className={classes.wrappedField}
                  fullWidth
                  displayEmpty
                  options={riskLevels}
                  component={Fields.Select}
                  label={`Risk tolerance - ${
                    firstCustomer && firstCustomer.display_name
                  }`}
                  tooltip="Based on completed risk tolerance questionnaire"
                />
                <Field
                  name="risk_tolerance_customer_2"
                  shrink
                  className={classes.wrappedField}
                  fullWidth
                  validate={Fields.Validators.required}
                  displayEmpty
                  options={riskLevels}
                  component={Fields.Select}
                  label={`Risk tolerance - ${
                    secondCustomer && secondCustomer.display_name
                  }`}
                  tooltip="Based on completed risk tolerance questionnaire"
                />
              </>
            ) : (
              <Field
                name="risk_tolerance"
                shrink
                className={classes.wrappedField}
                fullWidth
                displayEmpty
                options={riskLevels}
                component={Fields.Select}
                label="Risk tolerance"
                tooltip="Based on completed risk tolerance questionnaire"
              />
            )}

            {hasRiskRequirementCapacityEnabled && (
              <FieldPercent
                name="risk_capacity"
                shrink
                clearfieldinform="riskProfile"
                className={classes.wrappedField}
                fullWidth
                validate={[Fields.Validators.isPercentageValid]}
                startAdornment="-"
                component={Fields.Text}
                label="Risk capacity"
                tooltip="Maximum one-off loss that could be sustained on invested assets without compromising financial planning goal(s)"
              />
            )}

            <Field
              name="knowledge_level"
              shrink
              className={classes.wrappedField}
              fullWidth
              displayEmpty
              options={knowledgeAndExperience}
              component={Fields.Select}
              label="Knowledge & experience"
            />

            {values.knowledge_level && Number(values.knowledge_level) !== 1 && (
              <Field
                name="knowledge_level_description"
                shrink
                className={classNames(
                  classes.wrappedField,
                  classes.labelNoWrap
                )}
                fullWidth
                component={Fields.Text}
                multiline
                minRows={1}
                label="Knowledge & experience: additional comments"
                tooltip="Optional"
              />
            )}
          </div>

          <Paper
            warn
            className={classNames(classes.riskWrapper, classes.roundedBox)}
          >
            <Field
              name="recommended_risk_profile"
              shrink
              fullWidth
              className={classes.riskProfile}
              displayEmpty
              red
              options={riskLevels}
              component={Fields.Select}
              tooltipTop={-16}
              label="Recommended risk profile"
            />
          </Paper>
        </BoxWrapper>
      </Form>
      <Modal
        closeModalFunc={() => setSendEmail(false)}
        isOpen={sendEmail}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
      >
        <QuestionnaireRequest
          customers={caseCustomers as CustomerType[]}
          onClose={() => setSendEmail(false)}
          createRiskProfile={createRiskProfile}
        />
      </Modal>
    </Grid>
  );
};

export default compose(
  reduxForm({
    form: 'riskProfile',
    initialValues: {
      risk_capacity: null,
      time_horizon: null,
      recommendedRiskProfile: null
    },
    enableReinitialize: false
  }),
  connect((state: any) => ({
    knowledgeAndExperience:
      selectors.metaSelectors.knowledgeAndExperience(state),
    organizationData: selectors.authSelectors.organizationData(state),
    caseCustomers: selectors.caseSelectors.caseCustomers(state),
    riskLevels: selectors.metaSelectors.riskLevels(state),
    values: getFormValues('riskProfile')(state),
    cipThresHolds: selectors.metaSelectors.cipThresHolds(state),
    timeHorizons: selectors.metaSelectors.timeHorizons(state),
    hasCapacityForLossEnabled:
      selectors.capacityForLossSelectors.hasCapacityForLossEnabled(state),
    hasRiskRequirementCapacityEnabled:
      selectors.capacityForLossSelectors.hasRiskRequirementCapacityEnabled(
        state
      )
  }))
)(RiskProfile);
